import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import PublicTrack from "@/pages/PublicTrack";
import PublicReturnForm from "@/pages/PublicReturnForm";
import PublicWarrantyForm from "@/pages/PublicWarrantyForm";
import PublicManualOrderForm from "@/pages/PublicManualOrderForm";
import PublicReviewForm from "@/pages/PublicReviewForm";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import Products from "@/pages/Products";
import Tasks from "@/pages/Tasks";
import Notes from "@/pages/Notes";
import Integrations from "@/pages/Integrations";
import Team from "@/pages/Team";
import ReturnsWarranties from "@/pages/ReturnsWarranties";
import ReturnDetail from "@/pages/ReturnDetail";
import Reports from "@/pages/Reports";
import Automations from "@/pages/Automations";
import Messages from "@/pages/Messages";
import Messenger from "@/pages/Messenger";
import Creatives from "@/pages/Creatives";
import LandingPages from "@/pages/LandingPages";
import Ads from "@/pages/Ads";
import SocialPosts from "@/pages/SocialPosts";
import EmailTemplates from "@/pages/EmailTemplates";
import MailPage from "@/pages/MailPage";
import WhatsAppPage from "@/pages/WhatsAppPage";
import Imports from "@/pages/Imports";
import Calculations from "@/pages/Calculations";
import QrCodes from "@/pages/QrCodes";
import Reviews from "@/pages/Reviews";
import ApeluriRecenzii from "@/pages/ApeluriRecenzii";
import Invoices from "@/pages/Invoices";
import Customers from "@/pages/Customers";
import Financiar from "@/pages/Financiar";
import EmagCampaigns from "@/pages/EmagCampaigns";
import Company from "@/pages/Company";
import SettingsPage from "@/pages/Settings";
import EmagRmas from "@/pages/EmagRmas";
import Compensations from "@/pages/Compensations";
import MarketplaceOffers from "@/pages/MarketplaceOffers";
import MainOfferViolations from "@/pages/MainOfferViolations";

function ProtectedRoute({ children, page }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="flex min-h-screen items-center justify-center bg-zinc-950 text-zinc-500">Se încarcă...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (page && user.role !== "owner" && !user.allowed_pages?.includes(page)) {
    return <Navigate to={user.allowed_pages?.[0] ? `/${user.allowed_pages[0] === "orders" ? "" : user.allowed_pages[0]}` : "/tasks"} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      {/* Global toast portal — required for `toast.success/error/warning/loading`
          calls (sonner) throughout the app to actually render on screen. Was
          missing → every toast was silently dropped. */}
      <Toaster
        theme="dark"
        position="top-right"
        richColors
        closeButton
        expand
        toastOptions={{ style: { zIndex: 99999 } }}
      />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/track/:token" element={<PublicTrack />} />
            <Route path="/return-form" element={<PublicReturnForm />} />
            <Route path="/warranty-form" element={<PublicWarrantyForm />} />
            <Route path="/manual-order-form" element={<PublicManualOrderForm />} />
            <Route path="/review/:token" element={<PublicReviewForm />} />
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute page="orders"><Orders /></ProtectedRoute>} />
              <Route path="/products" element={<ProtectedRoute page="products"><Products /></ProtectedRoute>} />
              <Route path="/marketplace-offers" element={<ProtectedRoute page="products"><MarketplaceOffers /></ProtectedRoute>} />
              <Route path="/main-offer-violations" element={<ProtectedRoute page="products"><MainOfferViolations /></ProtectedRoute>} />
              <Route path="/tasks" element={<ProtectedRoute page="tasks"><Tasks /></ProtectedRoute>} />
              <Route path="/notes" element={<ProtectedRoute page="notes"><Notes /></ProtectedRoute>} />
              <Route path="/returns" element={<ProtectedRoute page="returns"><ReturnsWarranties /></ProtectedRoute>} />
              <Route path="/returns/:id" element={<ProtectedRoute page="returns"><ReturnDetail /></ProtectedRoute>} />
              <Route path="/emag-rmas" element={<ProtectedRoute page="returns"><EmagRmas /></ProtectedRoute>} />
              <Route path="/compensations" element={<ProtectedRoute page="returns"><Compensations /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute page="reports"><Reports /></ProtectedRoute>} />
              <Route path="/automations" element={<ProtectedRoute page="automations"><Automations /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute page="messages"><Messages /></ProtectedRoute>} />
              <Route path="/messenger" element={<ProtectedRoute page="messages"><Messenger /></ProtectedRoute>} />
              <Route path="/creatives" element={<ProtectedRoute><Creatives /></ProtectedRoute>} />
              <Route path="/landing-pages" element={<ProtectedRoute><LandingPages /></ProtectedRoute>} />
              <Route path="/ads" element={<ProtectedRoute><Ads /></ProtectedRoute>} />
              <Route path="/social-posts" element={<ProtectedRoute><SocialPosts /></ProtectedRoute>} />
              <Route path="/email-templates" element={<ProtectedRoute page="email_templates"><EmailTemplates /></ProtectedRoute>} />
              <Route path="/mail" element={<ProtectedRoute page="mail"><MailPage /></ProtectedRoute>} />
              <Route path="/whatsapp" element={<ProtectedRoute page="whatsapp"><WhatsAppPage /></ProtectedRoute>} />
              <Route path="/imports" element={<ProtectedRoute page="imports"><Imports /></ProtectedRoute>} />
              <Route path="/calculations" element={<ProtectedRoute><Calculations /></ProtectedRoute>} />
              <Route path="/qr-codes" element={<ProtectedRoute><QrCodes /></ProtectedRoute>} />
              <Route path="/reviews" element={<ProtectedRoute><Reviews /></ProtectedRoute>} />
              <Route path="/apeluri-recenzii" element={<ProtectedRoute><ApeluriRecenzii /></ProtectedRoute>} />
              <Route path="/invoices" element={<ProtectedRoute page="invoices"><Invoices /></ProtectedRoute>} />
              <Route path="/customers" element={<ProtectedRoute page="customers"><Customers /></ProtectedRoute>} />
              <Route path="/financiar" element={<ProtectedRoute><Financiar /></ProtectedRoute>} />
              <Route path="/emag-campaigns" element={<ProtectedRoute><EmagCampaigns /></ProtectedRoute>} />
              <Route path="/integrations" element={<ProtectedRoute page="integrations"><Integrations /></ProtectedRoute>} />
              <Route path="/company" element={<ProtectedRoute><Company /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
